/**
 * THEME API — palette brand del tenant: lettura e salvataggio.
 *
 * Contratto backend (FILO-54, impostazioni della company):
 *   - GET    /o/crm/v1_0/company/settings          → tutte le impostazioni della
 *     company, aperta a ogni utente. Di qui servono i due colori: una company
 *     che non li ha scelti li riceve VUOTI, perché il backend non conosce colori
 *     di default e la scelta è del client. In quel caso qui si restituisce null
 *     e decide il ThemeManager (cache → DEFAULT_PALETTE).
 *   - PATCH  /o/crm/v1_0/company/settings/palette  → salva i DUE colori scelti in
 *     Impostazioni → Azienda, senza toccare le altre impostazioni. Riservato agli
 *     amministratori (l'enforce è lato backend, come per il logo aziendale).
 *   - DELETE /o/crm/v1_0/company/settings/palette  → svuota i due colori e
 *     risponde a colori vuoti, cioè si torna ai predefiniti di qui. Stessa
 *     riserva agli amministratori.
 *
 * Il backend parla di `primaryColor`/`secondaryColor`, il resto dell'app di
 * `BrandSeed` (`primary`/`secondary`): la traduzione sta solo qui. Quel che
 * arriva valorizzato passa da `derivaPalette`, che calcola gli altri colori,
 * così il resto dell'app riceve sempre una palette completa.
 */

import { httpClient } from './api/httpClient';
import { DEFAULT_PALETTE, derivaPalette, type BrandPalette, type BrandSeed } from './brandPalette';

/** Le impostazioni della company, per la parte che serve al tema. */
type CompanySettingResponse = { primaryColor?: string; secondaryColor?: string };

/** Colori vuoti = nessuna scelta: null, e il default lo decide chi chiama. */
const toPalette = (data: CompanySettingResponse | null | undefined): BrandPalette | null =>
  data?.primaryColor
    ? derivaPalette({ primary: data.primaryColor, secondary: data.secondaryColor })
    : null;

export const themeApi = {
  getTheme: (): Promise<BrandPalette | null> =>
    httpClient
      .get<CompanySettingResponse | null>('/company/settings')
      .then((r) => toPalette(r.data)),

  updatePalette: (seed: BrandSeed): Promise<BrandPalette> =>
    httpClient
      .patch<CompanySettingResponse>('/company/settings/palette', {
        primaryColor: seed.primary,
        secondaryColor: seed.secondary ?? '',
      })
      .then((r) => toPalette(r.data) ?? DEFAULT_PALETTE),

  /**
   * Riporta la company ai colori predefiniti. Il backend risponde a colori vuoti,
   * cioè "nessuna scelta": qui si traduce in DEFAULT_PALETTE, che è la scelta del
   * client per quel caso. Restituire i colori concreti invece di null serve al
   * chiamante, che così applica la risposta senza dover conoscere il default.
   */
  deletePalette: (): Promise<BrandPalette> =>
    httpClient
      .delete<CompanySettingResponse>('/company/settings/palette')
      .then((r) => toPalette(r.data) ?? DEFAULT_PALETTE),
};
